import { FormValues as PostFormValues } from 'components/PostForm/PostForm';
import useLocalStorage from 'hooks/useLocalStorage';
import React, { useState } from 'react';

export type DraftPost = PostFormValues & {
  draftId: string;
};

export const GlobalStateContext = React.createContext<{
  expandedContentIds: string[];
  expandCollapseContent: (id: string) => void;
  isContentExpanded: (id: string) => boolean;
  draftPosts: DraftPost[];
  createOrUpdateDraftPost: (post: DraftPost) => void;
  getDraftPost: (id: string) => DraftPost | undefined;
  removeDraftPost: (id: string) => void;
}>({
  expandedContentIds: [],
  expandCollapseContent: () => {},
  isContentExpanded: () => false,
  draftPosts: [],
  createOrUpdateDraftPost: () => {},
  getDraftPost: () => undefined,
  removeDraftPost: () => {},
});

export const useGlobalState = () => React.useContext(GlobalStateContext);

export const GlobalStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    saveKeyInLocalStorage,
    getKeyFromLocalStorage,
    isSaved,
  } = useLocalStorage('draftPosts');

  const [expandedContentIds, setExpandedContentIds] = useState<string[]>([]);
  const [draftPosts, setDraftPosts] = useState<DraftPost[]>(
    isSaved ? ((getKeyFromLocalStorage() || []) as DraftPost[]) : []
  );

  const expandCollapseContent = (id: string) => {
    if (expandedContentIds.includes(id)) {
      setExpandedContentIds(expandedContentIds.filter((item) => item !== id));
    } else {
      setExpandedContentIds([...expandedContentIds, id]);
    }
  };

  const isContentExpanded = (id: string) => {
    return expandedContentIds.includes(id);
  };

  const createOrUpdateDraftPost = (post: DraftPost) => {
    const existingDraft = draftPosts.find((p) => p.draftId === post.draftId);
    if (existingDraft) {
      setDraftPosts(
        draftPosts.map((p) => (p.draftId === post.draftId ? post : p))
      );
    } else {
      setDraftPosts([...draftPosts, post]);
    }
    saveKeyInLocalStorage(draftPosts);
  };

  const removeDraftPost = (id: string) => {
    setDraftPosts(draftPosts.filter((post) => post.draftId !== id));
    saveKeyInLocalStorage(draftPosts);
  };

  const getDraftPost = (id: string) => {
    return draftPosts.find((post) => post.draftId === id);
  };

  return (
    <GlobalStateContext.Provider
      value={{
        expandedContentIds,
        expandCollapseContent,
        isContentExpanded,
        draftPosts,
        createOrUpdateDraftPost,
        getDraftPost,
        removeDraftPost,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

export default GlobalStateProvider;
